import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  getAuth,
  onAuthStateChanged,
  type User,
  type Auth,
} from "firebase/auth";
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  type AppCheck,
} from "firebase/app-check";

type FirebaseConfig = {
  apiKey: string;
  authDomain?: string;
  projectId: string;
  [k: string]: unknown;
};

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let appCheck: AppCheck | null = null;

export function getFirebaseConfig(): FirebaseConfig {
  const raw = import.meta.env.VITE_FIREBASE_CONFIG as string | undefined;
  if (!raw) {
    throw new Error("Missing VITE_FIREBASE_CONFIG");
  }
  return JSON.parse(raw) as FirebaseConfig;
}

export function getFirebaseApp(): FirebaseApp {
  if (app) return app;
  app = initializeApp(getFirebaseConfig());
  return app;
}

export function initAppCheck(): AppCheck | null {
  if (appCheck) return appCheck;

  const fbApp = getFirebaseApp();
  const debugToken = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN as
    | string
    | undefined;
  const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY as string | undefined;

  // Development: use debug token
  if (debugToken) {
    // Set the debug token before initializing App Check
    // This tells Firebase to use the debug provider
    (
      self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN: string | boolean }
    ).FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken === "true" ? true : debugToken;

    appCheck = initializeAppCheck(fbApp, {
      provider: new ReCaptchaEnterpriseProvider(siteKey || "placeholder"),
      isTokenAutoRefreshEnabled: true,
    });
    return appCheck;
  }

  // Production: require reCAPTCHA site key
  if (!siteKey) {
    console.warn("App Check disabled: missing VITE_RECAPTCHA_SITE_KEY");
    return null;
  }

  appCheck = initializeAppCheck(fbApp, {
    provider: new ReCaptchaEnterpriseProvider(siteKey),
    isTokenAutoRefreshEnabled: true,
  });

  return appCheck;
}

export function getFirebaseAuth(): Auth {
  if (auth) return auth;
  auth = getAuth(getFirebaseApp());
  return auth;
}

export function subscribeToAuth(cb: (user: User | null) => void) {
  return onAuthStateChanged(getFirebaseAuth(), cb);
}
